// Loads the startup data: what the page already knows (the user it has in
// memory or cached) first, then the rest from one GET bootstrap. Each section
// goes to its handler in startup order. A section named in `revalidate` is
// applied from what the page knows and still requested. beforeFetch is waited
// on before the first request (the netplay page joins its rooms there).
// Sections that failed, on the server or in their handler, are requested again
// on their own after a delay; a network error repeats the whole request.
// Each failure in a row doubles the delay up to maxRetryDelay, plus up to half
// again at random so pages that lost the server together (a deploy) don't all
// come back at the same moment. It keeps trying unless given a maxErrors, or
// until isCancelled() says a newer run took over.

import { BOOTSTRAP_SECTIONS } from 'app/ladderStartupEvents';

export class BootstrapCancelled extends Error {
	constructor() {
		super('Startup was replaced by a newer run');
		this.name = 'BootstrapCancelled';
	}
}

function inStartupOrder(names) {
	return BOOTSTRAP_SECTIONS.filter((name) => names.includes(name));
}

/**
 * Hands each section to its handler, in startup order. Returns the names
 * still to load, and whether any of them failed (on the server, in its
 * handler, or by being missing from a response that was asked for it). A
 * section the page's own payload simply left out is not a failure. If the
 * user doesn't load, nothing else runs yet: the other handlers expect myUser.
 */
async function applySections(payload, fields, handlers, { fromPage, revalidate }) {
	const sections = (payload && payload.sections) || {};
	const failedOnServer = (payload && payload.failed) || {};
	const remaining = [];
	let failed = false;
	for(const name of inStartupOrder(fields)) {
		if(!Object.prototype.hasOwnProperty.call(sections, name)) {
			remaining.push(name);
			if(!fromPage || Object.prototype.hasOwnProperty.call(failedOnServer, name)) {
				failed = true;
			}
			if(name === 'my_user') {
				return { remaining: inStartupOrder(fields), failed };
			}
			continue;
		}
		try {
			await handlers[name](sections[name]);
			if(fromPage && revalidate.includes(name)) {
				remaining.push(name);
			}
		} catch(error) {
			console.error(`bootstrap section ${name} failed`, error);
			remaining.push(name);
			failed = true;
			if(name === 'my_user') {
				return { remaining: inStartupOrder(fields), failed };
			}
		}
	}
	return { remaining, failed };
}

export async function runBootstrap({
	fields,
	embedded = null,
	revalidate = [],
	beforeFetch = () => Promise.resolve(),
	fetchSections,
	handlers,
	onProgress = () => {},
	retryDelay = 4000,
	maxRetryDelay = 60000,
	maxErrors = Infinity,
	random = Math.random,
	isCancelled = () => false,
	wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
}) {
	let pending = inStartupOrder(fields);
	let payload = embedded;
	let errors = 0;
	let readyToFetch = null;

	while(pending.length) {
		onProgress({ active: true, failedLastAttempt: false, errors, done: false });
		const fromPage = !!payload;
		if(!payload) {
			try {
				readyToFetch = readyToFetch || beforeFetch();
				await readyToFetch;
				payload = await fetchSections(pending);
			} catch(error) {
				console.error('bootstrap request failed', error);
			}
		}
		if(payload) {
			const applied = await applySections(payload, pending, handlers, { fromPage, revalidate });
			pending = applied.remaining;
			payload = null;
			if(!pending.length) {
				break;
			}
			if(!applied.failed) {
				continue;
			}
		}
		errors++;
		onProgress({ active: true, failedLastAttempt: true, errors, done: false });
		if(errors > maxErrors) {
			throw new Error('Startup Failed Multiple Times');
		}
		console.error('error in startup, retrying in a few seconds', pending);
		const backedOff = Math.min(retryDelay * 2 ** (errors - 1), maxRetryDelay);
		await wait(Math.round(backedOff * (1 + random() / 2)));
		if(isCancelled()) {
			throw new BootstrapCancelled();
		}
	}
	onProgress({ active: false, failedLastAttempt: false, errors: 0, done: true });
}
